import { useCallback } from 'react';
import { productCacheHelpers } from '../utils/productCacheEvents';
import { Product } from '../types/product';

/**
 * Hook para facilitar a invalidação de cache quando produtos são modificados
 * 
 * Use este hook em páginas que fazem CRUD de produtos (ERP)
 * para garantir que o cache seja atualizado automaticamente
 */
export const useProductCacheInvalidation = () => {
  /**
   * Notifica que um produto foi criado
   */
  const notifyProductCreated = useCallback((productId: number, productData?: Product) => {
    productCacheHelpers.notifyProductCreated(productId, productData);
  }, []);

  /**
   * Notifica que um produto foi atualizado
   */
  const notifyProductUpdated = useCallback((productId: number, productData?: Product) => {
    productCacheHelpers.notifyProductUpdated(productId, productData);
  }, []);

  /**
   * Notifica que um produto foi deletado
   */
  const notifyProductDeleted = useCallback((productId: number) => {
    productCacheHelpers.notifyProductDeleted(productId);
  }, []);

  /**
   * Notifica que o estoque de um produto foi atualizado
   */
  const notifyStockUpdated = useCallback((productId: number, stockQuantity: number) => {
    productCacheHelpers.notifyStockUpdated(productId, { stock_quantity: stockQuantity });
  }, []);

  /**
   * Notifica que o grupo de um produto foi alterado
   */
  const notifyGroupChanged = useCallback((productId: number, oldGroupId?: number, newGroupId?: number) => {
    productCacheHelpers.notifyGroupChanged(productId, oldGroupId, newGroupId);
  }, []);

  /**
   * Notifica que o status de destaque foi alterado
   */
  const notifyFeaturedChanged = useCallback((productId: number, isFeatured: boolean) => {
    productCacheHelpers.notifyFeaturedChanged(productId, isFeatured);
  }, []);

  /**
   * Notifica atualização em massa de produtos
   */
  const notifyBulkUpdated = useCallback((productIds?: number[]) => {
    productCacheHelpers.notifyBulkUpdated(productIds);
  }, []);

  /**
   * Invalida todo o cache
   */
  const invalidateAll = useCallback(() => {
    productCacheHelpers.invalidateAll();
  }, []);

  return {
    notifyProductCreated,
    notifyProductUpdated,
    notifyProductDeleted,
    notifyStockUpdated,
    notifyGroupChanged,
    notifyFeaturedChanged,
    notifyBulkUpdated,
    invalidateAll,
  };
};
